import useLifecareStudioBody from '../../hooks/useLifecareStudioBody';
import DemandStudioPreview from '../demand-studio/DemandStudioPreview';
import DemandStudioSkeleton from '../demand-studio/DemandStudioSkeleton';
import DemandStudioTabBar from '../demand-studio/DemandStudioTabBar';
import LifecareStudioForm from './LifecareStudioForm';
import LifecareStudioCommentBar from './LifecareStudioCommentBar';

const previewContainerClasses = 'h-full';

// `relative` anchors the absolute-positioned per-tab comment bar to the body itself, so it
// floats over the scroll area instead of acting as a footer that pushes the form up.
const containerClasses = 'relative h-full min-h-0 flex flex-col';

const scrollbarClasses =
  '[&::-webkit-scrollbar]:w-0.5 ' +
  '[&::-webkit-scrollbar-track]:bg-transparent ' +
  '[&::-webkit-scrollbar-thumb]:bg-border ' +
  '[&::-webkit-scrollbar-thumb]:rounded-full';

// flex-1 fills the available height; pb-28 gives the form ~7rem of empty space at the
// bottom so the last bit of content scrolls clear of the floating comment bar.
const scrollAreaClasses = `flex-1 min-h-0 overflow-y-auto pr-2 pb-28 ${scrollbarClasses}`;

// The tab bar uses negative horizontal margin (-mx-6) to bleed to the card edges,
// so the form column needs to live inside a px-6 card. The pt-0 + rounded-tl-none /
// rounded-tr-none variant lets the tabs sit flush against the card's top edge.
const formWrapperBase =
  'bg-white border border-border rounded-2xl p-6 min-h-full pt-0! rounded-tl-none! rounded-tr-none!';

// Floats inside the relative container. pointer-events-none on the wrapper lets
// clicks pass through the empty side gutters (so the user can still grab the
// scrollbar there) while the bar itself re-enables pointer events.
const floatingBarWrapperClasses =
  'absolute inset-x-4 bottom-4 z-10 pointer-events-none';
const floatingBarInnerClasses = 'pointer-events-auto max-w-4xl mx-auto';

const emptyStateClasses =
  'flex flex-col items-center justify-center h-full text-center text-subtle gap-2';

const LifecareStudioEmptyState = () => (
  <div className={emptyStateClasses}>
    <i className="fa fa-folder-open text-3xl" />
    <h3 className="m-0 font-semibold">No lifecare plans yet</h3>
    <p className="m-0 text-sm">
      This case does not have a generated structured lifecare plan to edit.
    </p>
  </div>
);

const LifecareStudioBody = ({
  isInitialLoadPending,
  hasAnyLifecareClient,
  showPreview,
  isPreviewStale,
  isNewPlanForSelectedClient,
  formState,
  previewState,
  suggestedEditsState,
  tabState,
}) => {
  const {
    tabs,
    activeTab,
    activeTabType,
    handleTabClick,
    tabBarRef,
    stickyOffsetStyle,
    activeSection,
    activeSectionUiState,
    updateSectionComment,
    handleRegenerateSection,
    isSuggestedEditsLoading,
  } = useLifecareStudioBody({ suggestedEditsState, tabState });
  if (isInitialLoadPending) return <DemandStudioSkeleton />;
  if (!hasAnyLifecareClient) return <LifecareStudioEmptyState />;
  if (showPreview) {
    return (
      <div className={previewContainerClasses}>
        <DemandStudioPreview
          previewState={previewState}
          hideSelectors
          forceSkeleton={isPreviewStale}
        />
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className={scrollAreaClasses}>
        <div className={formWrapperBase} style={stickyOffsetStyle}>
          {tabs && (
            <DemandStudioTabBar
              containerRef={tabBarRef}
              tabs={tabs}
              activeTabType={activeTabType}
              onTabClick={handleTabClick}
            />
          )}
          <LifecareStudioForm
            formState={formState}
            isNewPlan={isNewPlanForSelectedClient}
            activeTab={activeTab}
          />
        </div>
      </div>
      <div className={floatingBarWrapperClasses}>
        <div className={floatingBarInnerClasses}>
          <LifecareStudioCommentBar
            activeTab={activeTab}
            section={activeSection}
            uiState={activeSectionUiState}
            updateSectionComment={updateSectionComment}
            handleRegenerateSection={handleRegenerateSection}
            disabled={isSuggestedEditsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default LifecareStudioBody;
